import React, {memo, useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Skeleton,
    Stack,
    Text,
    VStack,
    Link,
    useToast
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import {CavendersIcon, CopyIcon, StarIcon} from '../icons'
import {
    amountContainerStyle,
    copyIconStyle,
    copyPromoCodeStyle,
    couponCardHeadingIconStyle,
    couponCardStyle,
    couponIconStyle,
    couponValueStyle,
    discountValueStyle,
    promoCardExpiryTextStyle,
    promoCardNoteTextStyle,
    promoCodeStyle,
    promoHeadingStyle,
    rewardTextStyle
} from './index.style'

export const CouponCard = memo(
    ({
        couponCode,
        discountValue,
        expiry,
        currency = 'USD',
        onOpen,
        setSelectedCoupon,
        isLoading = true,
        isModalView = false
    }) => {
        const {formatDate, formatMessage, formatNumber} = useIntl()
        const toast = useToast()
        const currencySymbol = useMemo(() =>
            formatNumber(1, {
                style: 'currency',
                currency
            })?.replace(/[0-9.]/g, '')
        )
        const handleCopy = useCallback(async (e) => {
            e.preventDefault()
            e.stopPropagation()
            try {
                await navigator.clipboard.writeText(couponCode)
                toast({
                    title: formatMessage({
                        defaultMessage: 'Copied!',
                        id: 'my_cavenders.coupon_card.success_copied'
                    }),
                    status: 'success',
                    duration: 3000,
                    isClosable: true
                })
            } catch (err) {
                console.error('Failed to copy: ', err)
            }
        }, [])
        const handleOnCouponCardClick = useCallback(() => {
            if (!isLoading) {
                onOpen?.()
                setSelectedCoupon?.({couponCode, discountValue, currency, expiry})
            }
        }, [isLoading])
        return (
            <Skeleton
                as={VStack}
                {...couponCardStyle(isModalView)}
                onClick={handleOnCouponCardClick}
                isLoaded={!isLoading}
            >
                <CavendersIcon {...couponCardHeadingIconStyle(isModalView)} />
                <Stack sx={couponValueStyle(isModalView)}>
                    <StarIcon {...couponIconStyle(isModalView)} />
                    <Stack sx={amountContainerStyle(isModalView)}>
                        <Text as="span" className="currency-symbol">
                            {currencySymbol}
                        </Text>
                        <Text {...discountValueStyle(isModalView, currencySymbol)}>
                            {discountValue}
                        </Text>
                    </Stack>
                </Stack>
                <VStack spacing="10px" mt="10px !important">
                    <Text {...rewardTextStyle(isModalView)}>
                        <FormattedMessage
                            defaultMessage="Reward"
                            id="my_cavenders.coupon_card.reward"
                        />
                    </Text>
                    <Text {...promoHeadingStyle(isModalView)}>
                        <FormattedMessage
                            defaultMessage="Promo code"
                            id="my_cavenders.coupon_card.reward.promo_code"
                        />
                        :
                    </Text>
                    <Text {...promoCodeStyle(isModalView)}>{couponCode}</Text>
                    <Link {...copyPromoCodeStyle(isModalView)} onClick={handleCopy}>
                        <CopyIcon {...copyIconStyle(isModalView)} />
                        <FormattedMessage
                            defaultMessage="Copy Code"
                            id="my_cavenders.coupon_card.copy_code"
                        />
                    </Link>
                </VStack>
                <Text {...promoCardExpiryTextStyle(isModalView)}>
                    <FormattedMessage
                        defaultMessage="Expires: {date}"
                        id="my_cavenders.coupon_card.expiry"
                        values={{
                            date: formatDate(new Date(expiry), {
                                month: '2-digit',
                                day: '2-digit',
                                year: 'numeric'
                            })
                        }}
                    />
                </Text>
            </Skeleton>
        )
    }
)

CouponCard.propTypes = {
    couponCode: PropTypes.string.isRequired,
    discountValue: PropTypes.number.isRequired,
    expiry: PropTypes.string,
    currency: PropTypes.string,
    onOpen: PropTypes.func,
    setSelectedCoupon: PropTypes.func,
    isModalView: PropTypes.bool,
    isLoading: PropTypes.bool
}

CouponCard.displayName = 'CouponCard'
